import type { Language } from '@eformulieren/engine'
import type { Bilingual } from '@eformulieren/renderer'

/**
 * Een handeling die de pod weigerde, vertaald naar een melding die de invuller begrijpt (R-143,
 * R-157, ontwerp 11.4).
 *
 * **Twee standen en niet één.** Komt het formulier niet op, dan is er niets te tonen en zegt de
 * pagina dat (`FormPage`, de foutstand). Weigert de pod een handeling (indienen, een
 * oordeel), of lukt het wegschrijven voor een sprong in de reeks niet, dan staat het formulier er
 * wél en blijft het staan: de melding komt bij de actiebalk. Tot deze opdracht vielen de twee
 * samen, en dan verving "Het formulier kwam niet op: POST /indienen gaf 409: {...}" een formulier
 * dat gewoon was opgekomen.
 *
 * **De code in de stand, de tekst pas bij het tonen.** Zo wisselt de melding mee met de taal
 * (R-144) zonder dat er iets opnieuw hoeft te worden opgevraagd. De ruwe keten (methode, pad,
 * status, code en lijf) gaat naar `console.error`; naar het scherm gaat nooit een code of JSON.
 *
 * Hetzelfde patroon als de configurator (`melding()` in `apps/configurator/src/app/App.tsx`):
 * een bekende fout krijgt zijn eigen zin, al het andere een vaste, veilige tekst.
 */

/** Geen antwoord van de pod: de `fetch` zelf gooide. Geen code van de pod maar van deze pagina. */
export const NO_CONNECTION = 'geen-verbinding'

/** De sessie is verlopen: de code van de pod bij een 401, ook voor een handeling die daardoor niet meer kon. */
export const SESSION_EXPIRED_CODE = 'geen-geldig-token'

/** De sprong binnen de reeks ging niet door, omdat wegschrijven niet lukte (R-122). */
export const SWITCH_NOT_STORED = 'wissel-niet-opgeslagen'

/**
 * De codes die de handelingen van deze pagina kunnen krijgen, en wat de invuller of de toetser
 * ermee moet: indienen, een oordeel, een toetsoverdracht en een sprong in de reeks.
 *
 * De codes zelf komen uit de pod (`overdracht.ts`, `oordeel.ts`, `server.ts`). Een code die hier
 * niet staat krijgt de algemene tekst; een nieuwe weigering in de pod breekt deze pagina dus niet,
 * hij krijgt alleen nog geen eigen zin.
 */
const NOTICES: Readonly<Record<string, Bilingual>> = {
  'niet-aan-de-beurt': {
    nl: 'Dit kan nu niet: dit deel van het formulier is op dit moment niet aan de beurt.',
    en: 'This is not possible now: it is not the turn of this part of the form.',
  },
  // De volgende drie geeft de pod bij indienen én bij een toetsoverdracht (`overdracht.ts`), dus
  // ze noemen de handeling niet: "al ingediend" na een eigen goedkeuring zet de toetser op het
  // verkeerde been.
  'beurt-al-gesloten': {
    nl: 'Dit is al afgerond; het formulier is alleen nog na te lezen.',
    en: 'This turn has already been closed; the form can only be read.',
  },
  'harde-controles-open': {
    nl: 'Dit kan nog niet: er is nog iets in te vullen.',
    en: 'This is not possible yet: some conditions are still open.',
  },
  'geen-eigenaar-van-de-indiening': {
    nl: 'Alleen wie dit formulier in handen heeft, kan deze handeling doen.',
    en: 'Only the person who holds this form can do this.',
  },
  'oordeel-ontbreekt': {
    nl: 'Dit kan nog niet: elk criterium heeft eerst een oordeel nodig.',
    en: 'This is not possible yet: every criterion needs a judgement first.',
  },
  'criterium-voldoet-niet': {
    nl: 'Goedkeuren kan pas als elk criterium voldoet.',
    en: 'Approving is only possible once every criterion is met.',
  },
  'niets-om-te-corrigeren': {
    nl: 'Terugleggen kan alleen als minstens één criterium niet voldoet, met een onderbouwing.',
    en: 'Returning for correction is only possible when at least one criterion is not met, with a justification.',
  },
  // R-75, R-132: de aanvrager hoort te zien wat hij moet aanpassen, en dat staat in het
  // opmerkingveld van de toetslaag dat teruggaat. De knop staat in dit geval al uit; deze zin is
  // voor een opmerking die tussen het tonen van de knop en de klik werd leeggemaakt.
  'opmerking-ontbreekt': {
    nl: 'Terugleggen kan pas als er bij elke sectie die aanpassing vraagt een opmerking voor de aanvrager staat.',
    en: 'Returning for correction is only possible once every section that needs changes has a comment for the applicant.',
  },
  'geen-eigenaar-van-de-toetslaag': {
    nl: 'Alleen wie deze toets in handen heeft, kan hier een oordeel geven.',
    en: 'Only the person who holds this check can give a judgement here.',
  },
  'criterium-al-getoetst': {
    nl: 'Bij dit criterium staat al een oordeel.',
    en: 'This criterion already has a judgement.',
  },
  // Bij een 401 op een handeling staat bovenaan de melding van de verlopen sessie, met de knop
  // (`session.ts`); deze zin bij de actiebalk zegt dat de handeling zelf niet doorging. Hij beloofde
  // tot de opdracht van de verlopen sessie dat alles was bewaard, en dat wist deze pagina niet.
  [SESSION_EXPIRED_CODE]: {
    nl: 'Uw sessie is verlopen. Log opnieuw in en probeer het daarna nog eens.',
    en: 'Your session has expired. Sign in again and then try once more.',
  },
  'te-veel-verzoeken': {
    nl: 'Er komen even te veel verzoeken binnen. Probeer het over een minuut opnieuw.',
    en: 'Too many requests at the moment. Please try again in a minute.',
  },
  'inloggen-tijdelijk-niet-mogelijk': {
    nl: 'Inloggen is even niet mogelijk. Dat ligt niet aan u; probeer het straks opnieuw.',
    en: 'Signing in is not possible at the moment. This is not your fault; please try again later.',
  },
  [NO_CONNECTION]: {
    nl: 'De verbinding lukte niet. Probeer het opnieuw.',
    en: 'The connection failed. Please try again.',
  },
  [SWITCH_NOT_STORED]: {
    nl: 'U blijft bij dit dossier: uw invoer kon niet worden opgeslagen. Probeer het opnieuw.',
    en: 'You are staying on this file: your input could not be saved. Please try again.',
  },
}

const GENERAL: Bilingual = {
  nl: 'Dit is niet gelukt. Probeer het opnieuw.',
  en: 'This did not work. Please try again.',
}

/** De melding bij deze code, in deze taal; een onbekende of ontbrekende code krijgt de algemene. */
export function noticeForRejection(code: string | undefined, language: Language): string {
  const notice = (code === undefined ? undefined : NOTICES[code]) ?? GENERAL
  return notice[language]
}

/**
 * De code uit het lijf van een weigering (`{ code, melding, problemen }`, `server.ts`), of niets.
 *
 * Een lijf dat geen JSON is (een proxy die een HTML-pagina teruggeeft) of geen tekstcode draagt is
 * geen reden om iets te verzinnen: dan is het de algemene tekst.
 */
export function codeFromBody(body: string): string | undefined {
  try {
    const read = JSON.parse(body) as unknown
    if (typeof read !== 'object' || read === null) return undefined
    const code = (read as { code?: unknown }).code
    return typeof code === 'string' ? code : undefined
  } catch {
    return undefined
  }
}

/**
 * De ruwe keten van een geweigerde handeling, naar de console en nooit naar het scherm
 * (ontwerp 11.4: naar buiten een melding die de invuller begrijpt, in het log de hele keten).
 */
export function logRejection(chain: Readonly<Record<string, unknown>>): void {
  console.error('eformulieren: handeling geweigerd', chain)
}

/**
 * Leest de code uit een weigering van de pod, en logt de hele keten.
 *
 * Het lijf wordt hier één keer gelezen, voor de code en voor het log samen.
 */
export async function rejectionOf(
  method: string,
  path: string,
  response: Response,
): Promise<string | undefined> {
  const body = await response.text()
  const code = codeFromBody(body)
  logRejection({ methode: method, pad: path, status: response.status, code, lijf: body.slice(0, 2000) })
  return code
}

/** Een handeling die niet eens bij de pod aankwam: de `fetch` zelf gooide, met zijn oorzaak. */
export function rejectionWithoutResponse(method: string, path: string, error: unknown): string {
  logRejection({ methode: method, pad: path, fout: error, oorzaak: error instanceof Error ? error.cause : undefined })
  return NO_CONNECTION
}
